import { ReactNode } from "react";
import { Breadcrumb } from "antd";
import { Link } from "react-router-dom";
import styles from "./PageShell.module.css";

export type PageKind = "list" | "detail" | "dashboard" | "board";

export interface Crumb { label: string; to?: string; }
export interface MetaItem { label: string; value: ReactNode; }

interface Props {
  kind?: PageKind;
  kicker?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  crumbs?: Crumb[];
  meta?: MetaItem[];
  actions?: ReactNode;
  /** Rendered flush to the bottom edge of the header — e.g. <ProjectNav/>. */
  tabs?: ReactNode;
  children: ReactNode;
}

/**
 * The app's one page frame. `kind` is what makes a list, a detail, a dashboard
 * and the board read as four different things: it drives container width,
 * alignment and scroll behaviour, while the header stays identical so the app
 * still feels like one product.
 */
export const PageShell = ({
  kind = "list", kicker, title, subtitle, crumbs, meta, actions, tabs, children,
}: Props) => (
  <div className={styles.shell}>
    <header className={styles.header}>
      <div className={`${styles.headerInner} ${styles["headerInner_" + kind] || ""}`}>
        {crumbs && crumbs.length > 0 && (
          <Breadcrumb
            className={styles.crumbs}
            items={crumbs.map((c) => ({ title: c.to ? <Link to={c.to}>{c.label}</Link> : c.label }))}
          />
        )}
        {kicker && !crumbs && <div className="osi-kicker" style={{ marginBottom: 4 }}>{kicker}</div>}
        <div className={styles.titleRow}>
          <div style={{ minWidth: 0 }}>
            <h1 className={styles.title}>{title}</h1>
            {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
          </div>
          {actions && <div className={styles.actions}>{actions}</div>}
        </div>
        {meta && meta.length > 0 && (
          <div className={styles.meta}>
            {meta.map((m) => (
              <div className={styles.metaItem} key={m.label}>
                <span className="osi-kicker">{m.label}</span>
                <span className={styles.metaValue}>{m.value}</span>
              </div>
            ))}
          </div>
        )}
        {tabs && <div className={styles.tabs}>{tabs}</div>}
      </div>
    </header>
    <main className={`${styles.body} ${styles["body_" + kind]}`}>{children}</main>
  </div>
);

/** Filter/search row for list pages. Right-aligns a live result count. */
export const Toolbar = ({ children, count, unit = "items" }: { children?: ReactNode; count?: number; unit?: string }) => (
  <div className={styles.toolbar}>
    {children}
    <div className={styles.toolbarSpacer} />
    {typeof count === "number" && <span className={styles.count}>{count} {unit}</span>}
  </div>
);
